import React, { useState } from 'react';

import Header from './Header';
import Footer from './Footer';

import '../App.css';
import './VolunteerForm.css';

const REPORT_TYPES = [
  'Claim',
  'Organisation',
  'Material/Media',
  'Incident',
  'Other',
];

const SubmitForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    report_type: '',
    message: '',
    report_source: '',
    newsletter: false,
    _hp: '', // honeypot
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.email.trim()) {
      setError('Please provide your email address.');
      return;
    }
    if (!formData.message.trim()) {
      setError('Please describe what you want to submit.');
      return;
    }

    const payload = {
      source: 'report',
      name: formData.name.trim(),
      email: formData.email.trim(),
      message: formData.message.trim(),
      report_type: formData.report_type || null,
      report_source: formData.report_source.trim(),
      newsletter: formData.newsletter,
      _hp: formData._hp,
    };

    try {
      setIsLoading(true);
      const response = await fetch('/api/submissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setIsSubmitted(true);
        sessionStorage.setItem('hasSubmitted_report', 'true');
      } else {
        setError(result.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Could not connect. Please check your internet and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnotherReport = () => {
    setIsSubmitted(false);
    setFormData({ name: '', email: '', report_type: '', message: '', report_source: '', newsletter: false, _hp: '' });
    sessionStorage.removeItem('hasSubmitted_report');
  };

  // --- Thank-you state ---
  if (isSubmitted) {
    return (
      <>
        <Header />
        <div className="content-container">
          <h2 className="subheading">Thank you</h2>
          <div className="home-text">
            Thank you for your submission. We'll review it as soon as we can.
          </div>
          <br />
          <div className="home-text">Til liberation 🍉</div>
          <br />
          <button
            className="btn-green"
            onClick={handleAnotherReport}
          >
            Submit another
          </button>
        </div>
        <Footer />
      </>
    );
  }

  // --- Form ---
  return (
    <>
      <Header />
      <div className="content-container">
        <h2 className="subheading">Submit something</h2>

        <div className="home-text">
          Help us keep track of claims and fabrications by submitting something you'd
          like us to investigate — no matter how big or small.
        </div>

        <div className="home-text">
          We're building a centralised database tracking and debunking propaganda
          particularly made and/or repeated by officials, organisations, and
          media outlets, particularly post 7 October 2023 while the Israeli occupation
          carries out a genocide in Gaza.
        </div>

        <br />

        {error && (
          <div role="alert" style={{ color: '#be1515', marginBottom: '1rem', fontWeight: 'bold' }}>
            {error}
          </div>
        )}

        <form className="mt-5" onSubmit={handleSubmit}>
          {/* Honeypot */}
          <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
            <input
              type="text"
              name="_hp"
              value={formData._hp}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {/* Name */}
          <label className="font-semibold">
            Name
            <input
              className="w-full p-1 mt-[2.5%] mb-9"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              maxLength={200}
            />
          </label>
          <br />

          {/* Email */}
          <label className="font-semibold">
            Email address <span className="required-field">*</span>
            <input
              className="w-full p-1 mt-[2.5%] mb-9"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              maxLength={254}
            />
          </label>
          <br />

          {/* Report type */}
          <label>
            <b>What kind of thing is this?</b>
            <select
              className="w-full p-1 mt-[2.5%] mb-9"
              name="report_type"
              value={formData.report_type}
              onChange={handleChange}
            >
              <option value="">Select a type (optional)</option>
              {REPORT_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </label>
          <br />

          {/* Message — what is the claim / what are you reporting */}
          <label className="font-semibold">
            Describe it <span className="required-field">*</span><br />
            <span className="text-sm" style={{ fontWeight: 'normal' }}>
              Describe the claim, fabrication, or incident briefly.
            </span>
            <textarea
              className="w-full p-5 mt-[2.5%] mb-9"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              maxLength={5000}
              rows={6}
            />
          </label>

          {/* Where did you see it */}
          <label>
            <b>Where did you see it?</b><br />
            <span className="text-sm">
              Links, platform names, accounts — anything that helps us find it.
            </span>
            <textarea
              className="w-full p-5 mt-[2.5%] mb-9"
              name="report_source"
              value={formData.report_source}
              onChange={handleChange}
              maxLength={2000}
              rows={4}
            />
          </label>
          <br />

          {/* Newsletter */}
          <label className="flex items-start gap-3 mb-6">
            <input
              type="checkbox"
              name="newsletter"
              checked={formData.newsletter}
              onChange={handleChange}
              className="mt-1 min-w-4 h-4"
            />
            <span className="text-sm">
              <b>Sign up for news and updates from Hasbara Tracker</b><br />
              Get claims straight to your inbox.
            </span>
          </label>

          {/* Submit */}
          {isLoading ? (
            <p>Submitting...</p>
          ) : (
            <button className="btn-green" type="submit">
              Submit
            </button>
          )}
        </form>
      </div>
      <Footer />
    </>
  );
};

export default SubmitForm;
